/**
 * Utility functions for working with content in HTML/SVG
 */

// Regular expression to detect common HTML tags
export const HTML_TAGS_REGEX = /<\/?(?:div|i|strong|br|span|p|em|b|u|ul|ol|li)[^>]*>/i

/**
 * Determines if content should be considered "empty" for rendering purposes
 * Content is considered empty if it is null, an empty string, or contains only whitespace
 *
 * @param content - The content to check
 * @returns True if the content is empty, false otherwise
 */
export function isEmptyContent(content: string | number | null): boolean {
  return (
    content === null || content === '' || (typeof content === 'string' && content.trim() === '')
  )
}

/**
 * Checks if content contains HTML tags
 *
 * @param content - The content to check
 * @returns True if the content contains HTML tags, false otherwise
 */
export function containsHtmlTags(content: string): boolean {
  return HTML_TAGS_REGEX.test(content)
}

/**
 * Sets content to a DOM element, handling HTML tags appropriately
 * If the content contains HTML tags, uses innerHTML, otherwise uses textContent
 *
 * @param element - The element to set content on
 * @param content - The content to set
 */
export function setElementContent(element: HTMLElement, content: string): void {
  if (containsHtmlTags(content)) {
    // If the string contains HTML, use innerHTML (assume HTML is already escaped)
    element.innerHTML = content
  } else {
    // If it's just text, use textContent - browser will handle escaping automatically
    element.textContent = content
  }
}

/**
 * Calculates the appropriate font size to fit content within the available height
 *
 * @param element - The element to size
 * @param initialFontSize - The starting font size
 * @param containerWidth - The available width
 * @param containerHeight - The available height
 * @param tempContainer - Optional existing container to use for measurements
 * @returns The adjusted font size
 */
export function calculateFittingFontSize(
  element: HTMLElement,
  initialFontSize: number,
  containerWidth: number,
  containerHeight: number,
  tempContainer?: HTMLElement,
): number {
  // Create a temporary container for testing if one wasn't provided
  const useExistingContainer = !!tempContainer
  if (!tempContainer) {
    tempContainer = document.createElement('div')
    tempContainer.style.position = 'absolute'
    tempContainer.style.visibility = 'hidden'
    tempContainer.style.width = `${containerWidth}px`
    document.body.appendChild(tempContainer)
  }

  // Create test element with same content and initial styles
  const testElement = element.cloneNode(true) as HTMLElement
  tempContainer.appendChild(testElement)

  // Start with the initial font size and decrease until content height fits
  let currentFontSize = initialFontSize
  while (currentFontSize > 1 && tempContainer.offsetHeight > containerHeight) {
    currentFontSize -= 1
    testElement.style.fontSize = `${currentFontSize}px`
  }

  // Clean up temporary elements if we created them
  if (!useExistingContainer) {
    document.body.removeChild(tempContainer)
  }

  return currentFontSize
}
